import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useRbac } from "@/hooks/useRbac";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Check, X, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { formatApiErrorDetail } from "@/lib/api";
import { formatPatientDisplayUhid } from "@/lib/patientDisplay";
import { formatPatientAnatomyTypes } from "@/lib/anatomyTypes";
import { useModalities } from "@/contexts/ModalitiesContext";
import { getModalityBadgeClass } from "@/lib/modalities";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatTimestamp = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatStudyDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const isPatientDeletion = (item) => item?.type === "patient";

const getPatientUhid = (item) => item?.uhid || item?.patient_uhid || null;

const getPatientName = (item) => {
  const name = item?.name ?? item?.patient_name ?? item?.patient?.name;
  return typeof name === "string" && name.trim() ? name.trim() : null;
};

const getHospitalName = (item) => {
  const hospital = item?.institution_name
    ?? item?.organization
    ?? item?.patient?.institution_name
    ?? item?.patient?.organization;
  return typeof hospital === "string" && hospital.trim() ? hospital.trim() : null;
};

const getStudyLabel = (study) => {
  if (!study) return "study";
  if (study.description?.trim()) return study.description.trim();

  const anatomy = [study.anatomy_type_1, study.anatomy_type_2, study.anatomy_type_3]
    .filter((v) => typeof v === "string" && v.trim())
    .join(" / ");
  const dateLabel = formatStudyDate(study.study_date);
  const parts = [anatomy || study.body_part, dateLabel].filter(Boolean);
  if (parts.length) return parts.join(" · ");

  return formatPatientDisplayUhid(study) || "Untitled study";
};

const getPatientDetailsLabel = (patient) => {
  if (!patient) return "patient";
  const anatomy = formatPatientAnatomyTypes(patient);
  const doctor = patient.referring_doctor || patient.physician_name;
  const parts = [anatomy, doctor].filter((v) => typeof v === "string" && v.trim());
  if (parts.length) return parts.join(" · ");
  return getPatientName(patient) || formatPatientDisplayUhid(patient) || "Patient";
};

const getItemLabel = (item) =>
  isPatientDeletion(item) ? getPatientDetailsLabel(item) : getStudyLabel(item);

const PatientIdentityText = ({ item }) => {
  const name = getPatientName(item);
  const hospital = getHospitalName(item);
  const uhid = formatPatientDisplayUhid(item) || getPatientUhid(item);
  return (
    <>
      {name ? (
        <span className="font-medium text-foreground">{name}</span>
      ) : null}
      {hospital ? (
        <>
          {name ? ", " : null}
          <span className="font-medium text-foreground">{hospital}</span>
        </>
      ) : null}
      {uhid ? (
        <>
          {name || hospital ? " · " : null}
          <span className="font-mono font-medium text-foreground">{uhid}</span>
        </>
      ) : null}
    </>
  );
};

const getDeletionEndpoints = (item) => {
  if (isPatientDeletion(item)) {
    const key = getPatientUhid(item) || item?.id;
    return {
      approve: `${API}/patients/${encodeURIComponent(key)}/approve-deletion`,
      reject: `${API}/patients/${encodeURIComponent(key)}/reject-deletion`,
    };
  }
  return {
    approve: `${API}/studies/${encodeURIComponent(item.id)}/approve-deletion`,
    reject: `${API}/studies/${encodeURIComponent(item.id)}/reject-deletion`,
  };
};

export default function PendingStudyDeletions() {
  const navigate = useNavigate();
  const { canApproveStudyDeletion } = useRbac();
  const { getModality } = useModalities();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [loading, setLoading] = useState(true);
  const [itemToApprove, setItemToApprove] = useState(null);
  const [approving, setApproving] = useState(false);
  const [itemToReject, setItemToReject] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejecting, setRejecting] = useState(false);

  const fetchPending = useCallback(async (currentPage = page) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/studies/pending-deletions`, {
        params: { page: currentPage, limit },
      });
      setItems(res.data?.items ?? res.data?.studies ?? []);
      setTotal(res.data?.total ?? 0);
    } catch (e) {
      console.error(e);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load pending deletions");
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [limit, page]);

  useEffect(() => {
    if (!canApproveStudyDeletion) return;
    fetchPending(page);
  }, [canApproveStudyDeletion, fetchPending, page]);

  if (!canApproveStudyDeletion) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-muted-foreground" data-testid="pending-deletions-denied">
        You do not have permission to review pending deletions.
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil(total / limit));

  const handleConfirmApprove = async () => {
    if (!itemToApprove?.id && !getPatientUhid(itemToApprove)) return;
    setApproving(true);
    try {
      await axios.post(getDeletionEndpoints(itemToApprove).approve);
      toast.success(
        isPatientDeletion(itemToApprove) ? "Patient deletion approved" : "Study deletion approved"
      );
      setItemToApprove(null);
      await fetchPending(page);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to approve deletion");
    } finally {
      setApproving(false);
    }
  };

  const handleConfirmReject = async () => {
    if (!itemToReject?.id && !getPatientUhid(itemToReject)) return;
    setRejecting(true);
    try {
      const body = rejectReason.trim() ? { reason: rejectReason.trim() } : {};
      await axios.post(getDeletionEndpoints(itemToReject).reject, body);
      toast.success(
        isPatientDeletion(itemToReject) ? "Patient deletion rejected" : "Study deletion rejected"
      );
      setItemToReject(null);
      setRejectReason("");
      await fetchPending(page);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to reject deletion");
    } finally {
      setRejecting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="pending-deletions-page">
      <Card className="overflow-hidden">
        <div className="overflow-auto max-h-[calc(100vh-220px)]">
          <Table className="sticky-table min-w-[900px]">
            <TableHeader>
              <TableRow>
                <TableHead className="w-24">Type</TableHead>
                <TableHead className="w-56 min-w-[14rem]">Patient</TableHead>
                <TableHead>Details</TableHead>
                <TableHead className="w-40">Requested by</TableHead>
                <TableHead className="w-40">Requested at</TableHead>
                <TableHead className="w-28 text-center">Impact</TableHead>
                <TableHead className="w-28 text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10 text-muted-foreground">
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" /> Loading...
                    </span>
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10 text-muted-foreground">
                    No pending deletions
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item, i) => {
                  const isPatient = isPatientDeletion(item);
                  const itemLabel = getItemLabel(item);
                  const patientUhid = formatPatientDisplayUhid(item) || getPatientUhid(item) || "—";
                  const patientName = getPatientName(item);
                  const hospitalName = getHospitalName(item);
                  const navUhid = getPatientUhid(item);
                  const studiesCount = item.pending_studies_count ?? item.studies_count ?? 0;
                  const filesCount = item.pending_files_count ?? item.files_count ?? 0;
                  const reportsCount = item.pending_reports_count ?? item.reports_count ?? 0;
                  return (
                  <TableRow key={item.id || navUhid || i} data-testid={`pending-deletion-row-${i}`}>
                    <TableCell>
                      <Badge className={`text-[10px] font-normal border-transparent ${
                        isPatient ? "bg-sky-100 text-sky-800" : "bg-violet-100 text-violet-800"
                      }`}>
                        {isPatient ? "Patient" : "Study"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <button
                        type="button"
                        className="block w-full min-w-0 text-left"
                        onClick={() => {
                          if (navUhid) navigate(`/patients/${navUhid}`);
                        }}
                        aria-label={`Open patient ${patientName || patientUhid}`}
                      >
                        {patientName ? (
                          <div
                            className="truncate text-sm font-medium text-primary hover:underline"
                            title={patientName}
                          >
                            {patientName}
                          </div>
                        ) : null}
                        {hospitalName ? (
                          <div
                            className="mt-0.5 truncate text-xs text-muted-foreground"
                            title={hospitalName}
                          >
                            {hospitalName}
                          </div>
                        ) : null}
                        <div
                          className={`truncate font-mono text-xs ${
                            patientName
                              ? "mt-0.5 text-muted-foreground"
                              : "font-medium text-primary hover:underline"
                          }`}
                        >
                          {patientUhid}
                        </div>
                      </button>
                    </TableCell>
                    <TableCell className="font-medium min-w-[180px]">
                      <div className="truncate" title={itemLabel}>
                        {isPatient ? "Entire patient record" : itemLabel}
                      </div>
                      {isPatient && itemLabel !== "Patient" && (
                        <div className="text-xs text-muted-foreground truncate mt-0.5" title={itemLabel}>
                          {itemLabel}
                        </div>
                      )}
                      <div className="flex flex-wrap items-center gap-1 mt-1">
                        {!isPatient && item.modality ? (
                          <Badge className={`text-[10px] ${getModalityBadgeClass(item.modality)}`}>
                            {getModality(item.modality)?.label ?? item.modality}
                          </Badge>
                        ) : null}
                        <Badge className="text-[10px] font-normal border-transparent bg-amber-100 text-amber-800">
                          Pending deletion
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">
                      {item.pending_deletion_by_name || "—"}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatTimestamp(item.pending_deletion_at)}
                    </TableCell>
                    <TableCell className="text-center text-xs text-muted-foreground">
                      {isPatient ? (
                        <span>{studiesCount} {studiesCount === 1 ? "study" : "studies"}</span>
                      ) : (
                        <span>
                          {filesCount} files
                          <br />
                          {reportsCount} reports
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-center gap-1">
                        <button
                          type="button"
                          data-testid={`approve-pending-deletion-${i}`}
                          onClick={() => setItemToApprove(item)}
                          className="p-2 hover:bg-emerald-50 rounded-md"
                          title="Approve deletion"
                          aria-label={`Approve deletion of ${itemLabel}`}
                        >
                          <Check className="w-4 h-4 text-emerald-600" />
                        </button>
                        <button
                          type="button"
                          data-testid={`reject-pending-deletion-${i}`}
                          onClick={() => {
                            setRejectReason("");
                            setItemToReject(item);
                          }}
                          className="p-2 hover:bg-secondary rounded-md"
                          title="Reject deletion"
                          aria-label={`Reject deletion of ${itemLabel}`}
                        >
                          <X className="w-4 h-4 text-muted-foreground" />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
        <div className="border-t border-[var(--xl-line)] px-3 sm:px-4 py-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
          <p className="text-xs sm:text-sm text-muted-foreground">
            Page {page} of {totalPages} · {total} pending
          </p>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="secondary"
              size="icon"
              className="h-9 w-9"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={loading || page <= 1}
              aria-label="Previous page"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="icon"
              className="h-9 w-9"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={loading || page >= totalPages}
              aria-label="Next page"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </Card>

      <AlertDialog
        open={!!itemToApprove}
        onOpenChange={(open) => { if (!open && !approving) setItemToApprove(null); }}
      >
        <AlertDialogContent data-testid="approve-pending-deletion-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isPatientDeletion(itemToApprove) ? "Approve patient deletion?" : "Approve study deletion?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isPatientDeletion(itemToApprove) ? (
                <>
                  This will permanently delete patient{" "}
                  <PatientIdentityText item={itemToApprove} />
                  {" "}and{" "}
                  {itemToApprove?.pending_studies_count ?? itemToApprove?.studies_count ?? 0}{" "}
                  {(itemToApprove?.pending_studies_count ?? itemToApprove?.studies_count ?? 0) === 1
                    ? "linked study"
                    : "linked studies"}
                  . This cannot be undone.
                </>
              ) : (
                <>
                  This will permanently delete{" "}
                  <span className="font-medium text-foreground">
                    {getStudyLabel(itemToApprove)}
                  </span>
                  {(getPatientName(itemToApprove) || getHospitalName(itemToApprove) || getPatientUhid(itemToApprove)) && (
                    <>
                      {" "}for patient{" "}
                      <PatientIdentityText item={itemToApprove} />
                    </>
                  )}
                  {" "}and its files/reports
                  {" "}({itemToApprove?.pending_files_count ?? 0} files, {itemToApprove?.pending_reports_count ?? 0} reports).
                  This cannot be undone.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={approving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleConfirmApprove();
              }}
              disabled={approving}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              data-testid="approve-pending-deletion-confirm"
            >
              {approving ? "Approving..." : "Approve deletion"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={!!itemToReject}
        onOpenChange={(open) => {
          if (!open && !rejecting) {
            setItemToReject(null);
            setRejectReason("");
          }
        }}
      >
        <DialogContent className="max-w-md" data-testid="reject-pending-deletion-dialog">
          <DialogHeader>
            <DialogTitle>
              {isPatientDeletion(itemToReject) ? "Reject patient deletion" : "Reject study deletion"}
            </DialogTitle>
            <DialogDescription>
              {isPatientDeletion(itemToReject) ? (
                <>
                  Keep patient{" "}
                  <PatientIdentityText item={itemToReject} />
                  {" "}and clear the pending deletion request.
                </>
              ) : (
                <>
                  Keep{" "}
                  <span className="font-medium text-foreground">
                    {getStudyLabel(itemToReject)}
                  </span>
                  {(getPatientName(itemToReject) || getHospitalName(itemToReject) || getPatientUhid(itemToReject)) && (
                    <>
                      {" "}for patient{" "}
                      <PatientIdentityText item={itemToReject} />
                    </>
                  )}
                  {" "}and clear the pending deletion request.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="pending-reject-reason" className="text-xs">Reason (optional)</Label>
            <Textarea
              id="pending-reject-reason"
              data-testid="reject-pending-deletion-reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder={
                isPatientDeletion(itemToReject)
                  ? "Why this patient should be kept"
                  : "Why this study should be kept"
              }
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              disabled={rejecting}
              onClick={() => {
                setItemToReject(null);
                setRejectReason("");
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmReject}
              disabled={rejecting}
              data-testid="reject-pending-deletion-confirm"
            >
              {rejecting ? "Rejecting..." : "Reject deletion"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
